<template>
  <div class="dates-grid">
    <WeekDaysHeader class="dates-grid__header" :show-week-num-title="isWeek" />
    <div class="dates-grid__grid">
      <div
        v-for="row in rows"
        :class="[
          'dates-grid__row',
          {
            'is-select-week': isWeek,
            'is-selected': isWeek && isSelectedWeek(row),
          },
        ]"
        @click="onSelectCell(row[0])"
      >
        <div class="week-cell__container" v-if="isWeek">
          <slot name="week-cell" :week-num="row[0].weekNum">
            <div class="week-cell">
              {{ row[0].weekNum }}
            </div>
          </slot>
        </div>
        <div
          class="date-cell__container"
          v-for="cell in row"
          :key="cell.timestamp"
          @click.stop="onSelectCell(cell)"
        >
          <slot name="cell" :cell="cell">
            <div
              :class="[
                'date-cell',

                {
                  'no-current': cell.offsetMonth !== 'current',
                  'is-today': cell.isCurrent,
                  'is-weekend': cell.isWeekend,
                  'is-selected': cell.timestamp === timestamp,
                },
              ]"
            >
              {{ cell.text }}
            </div>
          </slot>
        </div>
      </div>
    </div>
    <div class="dates-grid__week-divider" v-if="isWeek" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { generateDateViewCells } from "../../generators";
import { WeekDaysHeader } from "../week-days-header";
import { modifyDate } from "../../utils";
import { DateViewCell } from "../../types";

const emit = defineEmits<{
  /**Событие выбора даты */
  (e: "update:modelValue", value: Date): void;
}>();

const props = defineProps({
  /**Выбранная дата */
  modelValue: { type: Date },
  /**Включить режим выбора недели */
  isWeek: { type: Boolean, default: false },
  /**Отображаемый год */
  viewYear: { type: Number, required: true },
  /**Отображаемый месяц */
  viewMonth: { type: Number, required: true },
});

/**Ячейки */
const rows = computed(() =>
  generateDateViewCells(props.viewYear, props.viewMonth)
);
/**Временная метка */
const timestamp = computed(
  () =>
    props.modelValue && modifyDate(props.modelValue).clearTime("hour").getTime()
);

/**
 * * Проверить выбрана ли неделя
 * @param cell Ячейка
 */
const isSelectedWeek = (cells: DateViewCell[]) => {
  return cells.some(
    (cell) => props.modelValue && timestamp.value === cell.timestamp
  );
};

const onSelectCell = (cell: DateViewCell) => {
  let date = new Date(cell.date);
  if (props.modelValue)
    date.setHours(
      props.modelValue.getHours(),
      props.modelValue.getMinutes(),
      props.modelValue.getSeconds(),
      props.modelValue.getMilliseconds()
    );
  if (props.isWeek) {
    date = modifyDate(date).getMonday();
  }
  emit("update:modelValue", date);
};
</script>

<style lang="scss" scoped>
.dates-grid {
  position: relative;
  display: flex;
  flex-direction: column;
  user-select: none;

  &__grid {
    display: flex;
    flex-direction: column;
  }
  &__row {
    display: grid;
    grid-template-columns: repeat(7, var(--tir-pm-datepicker__date-cell-size));
    border-radius: var(--tir-pm-datepicker__cell-br);

    &.is-select-week {
      grid-template-columns: var(--tir-pm-datepicker__week-cell-w) repeat(
          7,
          var(--tir-pm-datepicker__date-cell-size)
        );

      &:hover {
        background-color: var(--tir-pm-datepicker__cell-hover-bg);
      }

      &.is-selected {
        color: var(--tir-pm-datepicker__cell-selected-color);
        background-color: var(--tir-pm-datepicker__cell-selected-bg);

        &:hover {
          background-color: var(--tir-pm-datepicker__cell-hover-selected-bg);
        }

        .date-cell,
        .date-cell.no-current {
          color: var(--tir-pm-datepicker__cell-selected-color);
        }
      }
    }

    &:not(.is-select-week) {
      .date-cell {
        &:hover {
          background-color: var(--tir-pm-datepicker__cell-hover-bg);
        }
        &.is-selected {
          color: var(--tir-pm-datepicker__cell-selected-color);
          background-color: var(--tir-pm-datepicker__cell-selected-bg);

          &:hover {
            background-color: var(--tir-pm-datepicker__cell-hover-selected-bg);
          }
        }
      }
    }

    cursor: pointer;
  }
  &__header {
    margin-bottom: var(--tir-pm-datepicker__dates-grid-header-mb);
    margin-top: var(--tir-pm-datepicker__dates-grid-header-mt);
  }

  &__week-divider {
    position: absolute;
    top: var(--tir-pm-datepicker__dates-grid-header-mt);
    bottom: 0;
    width: 0.063rem;
    background-color: var(--tir-pm-datepicker__week-divider-color);
    left: var(--tir-pm-datepicker__week-cell-w);
  }
}

.date-cell {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tir-pm-datepicker__cell-color);
  text-align: center;
  font-size: var(--tir-pm-datepicker__cell-fs);
  font-weight: var(--tir-pm-datepicker__cell-fw);
  border-radius: var(--tir-pm-datepicker__cell-br);
  line-height: 1.5em;

  &:not(.is-selected).no-current {
    color: var(--tir-pm-datepicker__date-cell-no-current-color);
    &.is-weekend {
      color: var(--tir-pm-datepicker__date-cell-no-current-weekend-color);
    }
    &.is-today {
      color: var(--tir-pm-datepicker__date-cell-no-current-today-color);
    }
  }

  &.is-weekend {
    color: var(--tir-pm-datepicker__date-cell-weekend-color);
  }
  &.is-today {
    color: var(--tir-pm-datepicker__cell-current-color);
  }

  &__container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--tir-pm-datepicker__date-cell-size);
    cursor: pointer;
  }
}
.week-cell {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &__container {
    height: var(--tir-pm-datepicker__date-cell-size);
  }
}
</style>
